/**
 * useClearMatRoutes - Provides correct route paths for ClearMat pages
 *
 * Shared ClearMat pages (in src/pages/clearmat/) are used in three contexts:
 * 1. Marune app (ClearMat section): routes prefixed with /clearmat (e.g., /clearmat/school/123)
 * 2. Marune app (Schools section): routes prefixed with /schools (e.g., /schools/123)
 * 3. ClearMat standalone: routes at root (e.g., /school/123)
 *
 * This hook detects the current context and returns a path builder
 * so navigation works correctly in all environments.
 */

import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';

export function useClearMatRoutes() {
  const location = useLocation();

  const isMaruneSchoolContext = useMemo(() => {
    return location.pathname.startsWith('/schools/') || location.pathname === '/schools';
  }, [location.pathname]);

  const prefix = useMemo(() => {
    // If current URL starts with /clearmat, we're inside the Marune ClearMat section
    return location.pathname.startsWith('/clearmat') ? '/clearmat' : '';
  }, [location.pathname]);

  return {
    /** Route prefix: '/clearmat' in Marune ClearMat section, '' in standalone */
    prefix,
    /** Build a ClearMat path, e.g., clearMatPath('/school/123') */
    clearMatPath: (path: string) => `${prefix}${path}`,
    /** Directory/home path */
    directoryPath: isMaruneSchoolContext ? '/schools' : (prefix || '/directory'),
    /** School detail path */
    schoolPath: (slugOrId: string) => isMaruneSchoolContext
      ? `/schools/${slugOrId}`
      : `${prefix}/school/${slugOrId}`,
    /** School report path */
    reportPath: (slugOrId: string) => isMaruneSchoolContext
      ? `/schools/${slugOrId}/report`
      : `${prefix}/school/${slugOrId}/report`,
    /** School claim path */
    claimPath: (schoolId: string) => isMaruneSchoolContext
      ? `/schools/${schoolId}/claim`
      : `${prefix}/school/${schoolId}/claim`,
    /** School edit path */
    editPath: (schoolId: string) => isMaruneSchoolContext
      ? `/schools/${schoolId}/edit`
      : `${prefix}/school/${schoolId}/edit`,
  };
}
